import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { readTaskDetails } from '@timato/shared';
import { Button } from '@/components/ui';
import { LabelPicker } from '@/components/labels';
import { useAuth } from '@/auth/store';
import { useProjectMap, useTask } from '@/data/hooks';
import { projectColor } from '@/lib/projectColors';
import { formatDuration } from '@/lib/time';
import { releaseTask } from '@/team/locks';
import { selectMembers, useTeam } from '@/team/store';
import { isBreak } from '@/timer/engine';
import type { TimerApi } from '@/timer/useTimer';
import { GoalCombobox } from './GoalCombobox';
import { InFlightPanel } from './InFlightPanel';
import { TaskNotes } from './TaskNotes';
import { useFormats } from '@/lib/formats';

/**
 * Left column (8a): the goal field, then the task's logged time with a hairline progress bar, its labels, and the
 * cycle counter. Everything the user needs to know about *what* they are doing sits here, off the ring.
 */
export function GoalPanel({ api, children }: { api: TimerApi; /** Below the cycle line: the parked timers (TW-30, TW-32). */ children?: ReactNode }) {
  const { time } = useFormats();
  const { timer, actions, cycle, isCountdown, now } = api;
  const projects = useProjectMap();
  const task = useTask(timer.taskId);
  const project = timer.projectId ? projects.get(timer.projectId) : undefined;
  // What the session will be logged with: picked here for this session, or else the goal's (task plus project, 8e).
  const labelIds = timer.labelIds ?? [...new Set([...(task?.labelIds ?? []), ...(project?.labelIds ?? [])])];
  const hasGoal = !!(timer.taskId || timer.projectId || timer.goalText);
  const me = useAuth((s) => s.user?.id);
  const members = useTeam(selectMembers);
  const holder = task?.claimedBy ? members.find((m) => m.userId === task.claimedBy) : null;
  const ratio = task?.estimateSec ? Math.min(1, task.trackedSec / task.estimateSec) : null;

  return (
    <div className="w-full lg:w-[310px] flex flex-col gap-3 lg:self-start lg:mt-[34px]">
      {/* The shortcut that opens the field (G) is listed with the others in Settings, not repeated here. */}
      <p className="text-eyebrow text-ink-2 m-0">Goal</p>
      <GoalCombobox goal={{ taskId: timer.taskId, projectId: timer.projectId, goalText: timer.goalText }} onChange={(g) => void actions.chooseGoal(g)} />

      {task && (
        <>
          {/* Straight under the goal: what the task is about, before how much of it is done. */}
          <TaskNotes notes={readTaskDetails(task).notes} />
          <p className="flex items-center gap-2 text-[12px] font-bold text-ink-2 m-0">
            Logged <span className="font-mono text-ink">{formatDuration(task.trackedSec, { zero: '0m' })}</span>
            {task.estimateSec ? <span className="text-ink-3">/ {formatDuration(task.estimateSec)} est.</span> : <span className="text-ink-3">· no estimate</span>}
          </p>
          {ratio !== null && (
            <div className="h-[3px] rounded-[2px] bg-track overflow-hidden">
              {/* 3a: the logged bar carries the project's colour, like the ring. It reads as the accent only
                  because the mock's project happens to be tomato. Without a project it falls back to the accent. */}
              <div className="h-full rounded-[2px]" style={{ width: `${ratio * 100}%`, background: projectColor(project) }} />
            </div>
          )}
        </>
      )}

      {/* TW-6: add or drop a label without opening the task. It changes this session only; a break carries none, and
          an empty timer shows nothing until there is a goal to label. */}
      {!isBreak(timer.mode) && (hasGoal || labelIds.length > 0) && <LabelPicker value={labelIds} onChange={(ids) => void actions.setLabels(ids)} project={project ?? null} quiet />}

      {task?.claimedBy && (
        <p className="flex items-center justify-between gap-2 text-meta-sm text-ink-2 m-0">
          <span className="inline-flex items-center gap-[6px]"><Lock size={12} />{task.claimedBy === me ? 'Claimed by you' : `Locked to ${holder?.name ?? 'a teammate'}`}</span>
          {task.claimedBy === me && timer.status === 'idle' && <Button variant="ghost" size="sm" onClick={() => void releaseTask(task)}>Release</Button>}
        </p>
      )}

      {/* 9a: agent runs sit under the goal, below the person's own figures and above the cycle line. */}
      <InFlightPanel now={now} />

      {/* TW-28: Track has no cycle to count, so an idle Track run says nothing here. No empty line is kept for
          "Tracking since" either: it opened a gap above the parked timers, and starting changes that list anyway. */}
      {(isCountdown || (timer.startedAt && timer.status !== 'idle')) && (
        <p className="text-[12.5px] font-bold text-ink-2 leading-[1.5] m-0 mt-[10px]">
          {isCountdown ? (
            <><span className="text-ink">{cycle.session}</span> · {cycle.next}</>
          ) : (
            <>Tracking since <span className="font-mono text-ink">{time(new Date(timer.startedAt!))}</span></>
          )}
        </p>
      )}
      {children}
    </div>
  );
}
